import * as React from "react";
import { Ordering } from "@/types/base";
import { ChevronDown, ChevronUp } from "lucide-react";
import { SnapshotCheckbox } from "@/components/snapshots/SnapshotCheckbox";
import {
  SNAPSHOT_GRID_NAME_COLUMN_ID,
  getSnapshotGridFixedColumnStyle,
  getSnapshotGridNameColumnWidth,
  type SnapshotGridColumnWidths,
  type SnapshotGridFixedColumnsVisibility,
} from "./layout";
import { ColumnResizeHandle } from "./columnResizeHandle";
import { getNextSnapshotGridOrdering } from "@/modules/snapshots/grid/sortAndFilter";

interface Props extends SnapshotGridFixedColumnsVisibility {
  setOrdering: (ordering: Ordering) => void;
  ordering: Ordering;
  isAllSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  interactionDisabled?: boolean;
  columnWidths?: SnapshotGridColumnWidths;
  onColumnResize?: (columnId: string, width: number) => void;
}

export const SnapshotGridTableFixedHeader: React.FC<Props> = ({
  setOrdering,
  ordering,
  isAllSelected,
  onSelectAll,
  interactionDisabled = false,
  showSelectionColumn = true,
  showActionsColumn = true,
  columnWidths,
  onColumnResize,
}) => {
  const visibility = { showSelectionColumn, showActionsColumn };
  const nameWidth = getSnapshotGridNameColumnWidth(columnWidths);
  const handleClick = (field: string) => {
    if (interactionDisabled) return;
    setOrdering(getNextSnapshotGridOrdering(ordering, field));
  };

  return (
    <>
      <th
        scope="col"
        className="sticky top-0 z-40 bg-[#f1edea] py-3.5 pl-3 pr-1 text-center text-sm font-semibold text-gray-400"
        style={getSnapshotGridFixedColumnStyle("rowNumber")}
      >
        #
      </th>
      {showSelectionColumn ? (
        <th
          scope="col"
          className="sticky top-0 z-40 bg-[#f1edea] py-3.5 pl-4 pr-2 text-left text-sm font-semibold text-gray-900"
          style={getSnapshotGridFixedColumnStyle("selection", visibility)}
        >
          <SnapshotCheckbox
            checked={isAllSelected}
            onCheckedChange={onSelectAll}
            ariaLabel="Select all snapshots"
            disabled={interactionDisabled}
          />
        </th>
      ) : null}
      {showActionsColumn ? (
        <th
          scope="col"
          className="sticky top-0 z-40 bg-[#f1edea] px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
          style={getSnapshotGridFixedColumnStyle("actions", visibility)}
        >
          Actions
        </th>
      ) : null}
      <th
        scope="col"
        className="sticky top-0 z-40 cursor-pointer border-r border-gray-300 bg-[#f1edea] py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
        style={getSnapshotGridFixedColumnStyle("name", visibility, columnWidths)}
        onClick={() => handleClick("name")}
      >
        <div className="flex items-center">
          Snapshot name
          {ordering.order_by === "name" ? (
            ordering.descending ? (
              <ChevronDown className="ml-1 h-4 w-4" />
            ) : (
              <ChevronUp className="ml-1 h-4 w-4" />
            )
          ) : (
            <ChevronUp className="ml-1 h-4 w-4 text-gray-400" />
          )}
        </div>
        {onColumnResize && (
          <ColumnResizeHandle
            columnId={SNAPSHOT_GRID_NAME_COLUMN_ID}
            label="Snapshot name"
            width={nameWidth}
            onResize={onColumnResize}
          />
        )}
      </th>
    </>
  );
};
